/**
 * common.js
 */

import { createTheme, alpha } from "@mui/material/styles";


export function generateBaseTheme(paletteMode) {
  return createTheme({
    palette: { 
      mode: paletteMode, 
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        sm_md: 750,
        md: 960,
        lg: 1280,
        xl: 1920,
      },
    },    
  })
}


// generates a theme of shared definitions using the provided base theme's palette
export function updateCommonTheme(theme) {
    

  //#region PALETTE

  // MISC
  theme.palette.background.default = theme.palette.sys.surface;
  theme.palette.background.paper = theme.palette.sys.surfaceContainer;
  theme.palette.divider = theme.palette.sys.outlineVariant;


  // TEXT
  theme.palette.text.primary = theme.palette.sys.onSurface;
  theme.palette.text.secondary = theme.palette.sys.onSurfaceVariant;
  theme.palette.text.icon = theme.palette.sys.onSurface;

  //#endregion


  //#region TYPOGRAPHY

  const fontSizes = [
    10,
    12,
    14,
    16,
    18,
    20,
    24,
    30,
    36,
    48,
    60,
    72,
    96,
  ];

  // create sizes array by converting px to rem
  theme.typography.sizes = [];
  fontSizes.forEach(size => {
    theme.typography.sizes.push(theme.typography.pxToRem(size));
  });

  // h1
  theme.typography.h1.fontFamily = ['"Source Sans Pro"', 'sans-serif'].join(',');
  theme.typography.h1.fontSize = theme.typography.sizes[12];

  // h2
  theme.typography.h2.fontFamily = ['"Source Sans Pro"', 'sans-serif'].join(',');
  theme.typography.h2.fontSize = theme.typography.sizes[10];

  // h3
  theme.typography.h3.fontFamily = ['"Source Sans Pro"', 'sans-serif'].join(',');
  theme.typography.h3.fontSize = theme.typography.sizes[9];

  // h4
  theme.typography.h4.fontFamily = ['"Source Sans Pro"', 'sans-serif'].join(',');
  theme.typography.h4.fontSize = theme.typography.sizes[8];
  theme.typography.h4.fontWeight = theme.typography.fontWeightLight;

  // h5
  theme.typography.h5.fontFamily = ['"Source Sans Pro"', 'sans-serif'].join(',');
  theme.typography.h5.fontSize = theme.typography.sizes[6];

  // h6
  theme.typography.h6.fontFamily = ['"Source Sans Pro"', 'sans-serif'].join(',');
  theme.typography.h6.fontSize = theme.typography.sizes[5];
  theme.typography.h6.letterSpacing = '0em';

  // subtitle1
  theme.typography.subtitle1.fontSize = theme.typography.sizes[3];

  // subtitle2
  theme.typography.subtitle2.fontSize = theme.typography.sizes[2];

  // body1
  theme.typography.body1.fontSize = theme.typography.sizes[2];

  // body2
  theme.typography.body2.fontSize = theme.typography.sizes[2];

  // button
  theme.typography.button.fontSize = theme.typography.sizes[2];

  // caption
  theme.typography.caption.fontSize = theme.typography.sizes[1];
  theme.typography.caption.lineHeight = 1.3;

  // overline
  theme.typography.overline.fontSize = 9;

  //#endregion TYPOGRAPHY


  //#region BOX SHADOWS

  const boxShadowParts = [
    [],
    ["0px 2px 1px -1px", "0px 1px 1px 0px", "0px 1px 3px 0px"],
    ["0px 3px 1px -2px", "0px 2px 2px 0px", "0px 1px 8px 0px"],
    ["0px 3px 1px -2px", "0px 3px 4px 0px", "0px 1px 8px 0px"],
    ["0px 2px 4px -1px", "0px 4px 5px 0px", "0px 1px 10px 0px"],
    ["0px 3px 5px -1px", "0px 5px 8px 0px", "0px 1px 14px 0px"],
    ["0px 3px 5px -1px", "0px 6px 10px 0px", "0px 1px 18px 0px"],
    ["0px 4px 5px -2px", "0px 7px 10px 1px", "0px 2px 16px 1px"],
    ["0px 5px 5px -3px", "0px 8px 10px 1px", "0px 3px 14px 2px"],
    ["0px 5px 6px -3px", "0px 9px 12px 1px", "0px 3px 16px 2px"],
    ["0px 6px 6px -3px", "0px 10px 14px 1px", "0px 4px 18px 3px"],
    ["0px 6px 7px -4px", "0px 11px 15px 1px", "0px 4px 20px 3px"],
    ["0px 7px 8px -4px", "0px 12px 17px 2px", "0px 5px 22px 4px"],
    ["0px 7px 8px -4px", "0px 13px 19px 2px", "0px 5px 24px 4px"],
    ["0px 7px 9px -4px", "0px 14px 21px 2px", "0px 5px 26px 4px"],
    ["0px 8px 9px -5px", "0px 15px 22px 2px", "0px 6px 28px 5px"],
    ["0px 8px 10px -5px", "0px 16px 24px 2px", "0px 6px 30px 5px"],
    ["0px 8px 11px -5px", "0px 17px 26px 2px", "0px 6px 32px 5px"],
    ["0px 9px 11px -5px", "0px 18px 28px 2px", "0px 7px 34px 6px"],
    ["0px 9px 12px -6px", "0px 19px 29px 2px", "0px 7px 36px 6px"],
    ["0px 10px 13px -6px", "0px 20px 31px 3px", "0px 8px 38px 7px"],
    ["0px 10px 13px -6px", "0px 21px 33px 3px", "0px 8px 40px 7px"],
    ["0px 10px 14px -6px", "0px 22px 35px 3px", "0px 8px 42px 7px"],
    ["0px 11px 14px -7px", "0px 23px 36px 3px", "0px 9px 44px 8px"],
    ["0px 11px 15px -7px", "0px 24px 38px 3px", "0px 9px 46px 8px"],
  ];
  const shadowColor = theme.shadowParams.shadowColor;
  const fadeBase = theme.shadowParams.fadeBase;
  const fadeFactor = 1;
  const subtleShadowAmount = theme.shadowParams.subtleShadowAmount;
  const boxShadow = (i, color, factor, base) => {
    let part = boxShadowParts[i];
    if (i === 0) {
      return 'none';
    } else {
      return `${part[0]} ${alpha(color, base[0] * factor)}, `
      + `${part[1]} ${alpha(color, base[1] * factor)}, `
      + `${part[2]} ${alpha(color, base[2] * factor)}`;
    }
  }
  theme.shadows = [];
  boxShadowParts.forEach((part, i) => {
    theme.shadows[i] = boxShadow(i, shadowColor, fadeFactor, fadeBase);
  });
  theme.boxShadowMaker = (i, color, factor, base) => {
    if (!factor) {
      factor = fadeFactor;
    }
    if (!base) {
      base = fadeBase
    }
    return boxShadow(i, color, factor, base);
  }
  theme.subtleBoxShadow = [
    `0px 0px 5px 0px ${alpha(shadowColor, subtleShadowAmount)}`,
    `0px 0px 10px 0px ${alpha(shadowColor, subtleShadowAmount)}`,
    `0px 0px 20px 0px ${alpha(shadowColor, subtleShadowAmount)}`,
    `0px 0px 30px 0px ${alpha(shadowColor, subtleShadowAmount)}`,
  ];

  //#endregion


  //#region COMPONENTS

  theme.components = {
    MuiLink: {
      defaultProps: {
        underline: "hover",
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "unset"
        }
      }
    },    
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        startIcon: {
          marginRight: theme.spacing(0.5),
        },
      },
      variants: [
        {
          props: { variant: "contained", color: "info" },
          style: {
            backgroundColor: alpha(theme.palette.sys.primary, 0.2),
            color: theme.palette.primary.dark,
            "&:hover": {
              backgroundColor: alpha(theme.palette.sys.primary, 0.3),
            },
          }
        },
        {
          props: { variant: "contained", color: "grey" },
          style: {
            backgroundColor: alpha(theme.palette.grey.main, 0.8),
            color: theme.palette.grey.contrastText,
            "&:hover": {
              backgroundColor: alpha(theme.palette.grey.main, 0.8),
            }            
          }
        },
        {
          props: { variant: "outlined", color: "grey" },
          style: {
            color: theme.palette.text.primary,
            borderColor:
              theme.palette.mode === "light"
                ? "rgba(0, 0, 0, 0.23)"
                : "rgba(255, 255, 255, 0.23)",
            "&.Mui-disabled": {
              border: `1px solid ${theme.palette.action.disabledBackground}`
            },
            "&:hover": {
              borderColor:
                theme.palette.mode === "light"
                  ? "rgba(0, 0, 0, 0.23)"
                  : "rgba(255, 255, 255, 0.23)",
              backgroundColor: alpha(
                theme.palette.text.primary,
                theme.palette.action.hoverOpacity
              )
            }
          }
        },
        {
          props: { color: "grey", variant: "text" },
          style: {
            color: theme.palette.text.primary,
            "&:hover": {
              backgroundColor: alpha(
                theme.palette.text.primary,
                theme.palette.action.hoverOpacity
              )
            }
          }
        }
      ]
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: 14,
          backgroundColor: theme.palette.sys.surfaceContainer,
          '&.Mui-disabled': {
            // fix safari disabled text bug (is fixed in mui v5)
            WebkitTextFillColor: theme.palette.text.disabled,
          }
        },
        multiline: {
          '&::after': {
            pointerEvents: 'none',
            content: '""',
            position: 'absolute',
            height: 17,
            width: 17,
            bottom: 8,
            right: 8,
            background: (() => {
              const bg = theme.palette.sys.surfaceContainer;
              const line = theme.palette.sys.outline;
              return `linear-gradient(135deg, ${bg} 0px, ${bg} 12px, `
              + `${line} 12.5px, ${line} 13px, ${bg} 13.5px, ${bg} 15px, `
              + `${line} 15.5px, ${line} 16px, ${bg} 16.5px, ${bg} 18px, `
              + `${line} 18.5px, ${line} 19px, ${bg} 19.5px, ${bg} 21px, `
              + `${line} 21.5px, ${line} 22px, ${bg} 22.5px`
              + `)`
            })(),
          }
        },
        inputMultiline: {
          lineHeight: '1.3876em',
          resize: 'vertical'
        }
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        multiline: {
          '&.MuiInputBase-sizeSmall': {
            paddingBottom: '8px',
            paddingRight: '8px',
          }
        }
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          boxShadow: theme.shadows[4],
          fontSize: theme.typography.sizes[1],
          fontWeight: theme.typography.fontWeightRegular,
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        message: {
          flex: 1,
        },
        action: {
          paddingLeft: theme.spacing(1)
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          '&:last-child': {
            paddingBottom: theme.spacing(2),
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${theme.palette.divider}`
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          // fix for weird issue with random thick looking dividers
          height: '0.5px',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: theme.spacing(0.5),
          '&.Mui-disabled': {
            opacity: 0.6
          },
          [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(1)
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paperFullWidth: {
          '&:not(.MuiDialog-paperFullScreen)': {
            [theme.breakpoints.down('sm')]: {
              width: `calc(100% - 32px)`,
              margin: theme.spacing(2)
            }
          }
        }
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.sys.primary,
          color: theme.palette.sys.onPrimary,
          [theme.breakpoints.down('sm')]: {
            padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
            '& .MuiTypography-root': {
              fontSize: theme.typography.sizes[4],
            }
          }
        }
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          // See https://github.com/mui/material-ui/issues/27851
          '.MuiDialogTitle-root+&': {
            paddingTop: 20,
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          color: theme.palette.sys.onSurface,
          '&.Mui-disabled': {
            color: theme.palette.action.disabled,
            backgroundColor: theme.palette.action.disabledBackground
          }
        }
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          margin: 0
        },
        option: {
          '&[aria-selected="true"][data-focus="true"]': {
            backgroundColor: theme.palette.action.selected,
          }
        }
      },
    },
    MuiAvatar: {
      styleOverrides: {
        colorDefault: {
          backgroundColor: theme.palette.sys.surfaceContainerHighest,
          color: theme.palette.sys.onSurfaceVariant
        }
      },
    },
    MuiTypography: {
      variants: [{
        props: { variant: 'subtext-small' },
        style: {
          display: 'block',
          fontSize: theme.typography.sizes[1],
          color: theme.palette.sys.onSurfaceVariant,
          lineHeight: 1.4,
          maxWidth: '75ch',
        }
      }, {
        props: { variant: 'subtext-large' },
        style: {
          display: 'block',
          fontSize: theme.typography.sizes[2],
          color: theme.palette.sys.onSurfaceVariant,
          lineHeight: 1.5,
          maxWidth: '75ch',
        }
      }]
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          textTransform: 'uppercase',
          fontSize: theme.typography.sizes[1],
          '&:hover': {
            cursor: 'default',
          },
        },
      },
    },
  }

  //#endregion


  //#region GLOBAL STYLES

  theme.globalStyles = {
    // this body rule prevents an obscure product tour bug that scrolls the content under the drawer.
    // conditions:
    // - drawer open
    // - product tour beacon is below the fold
    // - a MUI menu has been opened & closed (temp DOM element added & removed after the tour DOM elements)
    // - scrollbar manually dragged (its size and position should be in the wrong place at this point)
    'body': {
      overflow: 'hidden',
      '& .intercom-launcher': {
        boxShadow: theme.shadows[5],
      },
      // responsivley hide intercom launcher on mobile
      [theme.breakpoints.down('sm')]: {
        '& .intercom-launcher, & .intercom-launcher-frame': {
          display: 'none !important'
        },
        '& .intercom-launcher-force-show': {
          display: 'block !important',
        },
      }
    },
    '.element-default-box-shadow': {
      boxShadow: theme.subtleBoxShadow[2],
    },
    '.indent': {
      marginLeft: theme.spacing(2)
    },
    '.flex-c': {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    '.flex-hc': {
      display: 'flex',
      alignItems: 'center',
    },
    '.flex-fh': {
      flex: 1,
      display: 'flex',
      flexDirection: 'column'
    },
    '.absolute-fill': {
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    },
    '.bold': {
      fontWeight: 'bold',
    },

    // outlined adorned-end input has extra padding
    '.MuiOutlinedInput-adornedEnd': {
      paddingRight: 'unset'
    },

    // flash animation
    '@keyframes flash': {
      '0%': {
        opacity: 1,
      },

      '20%': {
        opacity: 1,
      },

      '50%': {
        opacity: 0.4,
      },

      '80%': {
        opacity: 1,
      },

      '100%': {
        opacity: 1,
      },
    },
    '.animate-flash': {
      animationName: 'flash',
      animationDuration: '1.5s',
      animationTimingFunction: 'ease-out',
      animationIterationCount: 'infinite',
      animationDirection: 'normal',
      animationPlayState: 'running',      
    }, 
  };

  //#endregion


  //#region SIZES

  theme.size = {};
  // TODO: Remove comment
  theme.size.contentMax = 980;
  // lightTheme.size.contentMax = 1960;
  theme.size.menuDrawerWidth = 280;
  theme.size.notificationDrawer = 500;
  theme.size.menuDrawer = {
    menuPadding: 20,
    // desktop params
    width: '75%',
    minWidth: theme.breakpoints.values.md,
    maxWidth: theme.breakpoints.values.lg,
    leftPanelMinWidth: 350,
    leftPanelMaxWidth: 400,
    arrowWidth: 16,
    // 0 - 600, 750 - 960
    // used to target when drawer content panel is mobile width
    mediaRuleMobileContent: `${theme.breakpoints.between('sm_md', 'md')}, ${theme.breakpoints.down('sm').replace('@media', '')}`,
  };
  theme.size.avatarSquare = 300;

  theme.spacing.dialogTitleIconMargin = theme.spacing(1);

  //#endregion


  //#region TRANSITIONS

  theme.transitions.duration.drawer = 350;
  theme.transitions.duration.page = 350;

  //#endregion
}